import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, Alert, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { uptimeAPI } from '../services/api';

export const STATUS = {
  met: { label: 'Met', color: 'success' },
  on_track: { label: 'On track', color: 'success' },
  at_risk: { label: 'At risk', color: 'warning' },
  missed: { label: 'Missed', color: 'error' },
  no_data: { label: 'No data', color: 'default' },
};
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const HOURLY_NOTE = 'Sumo only keeps the full 2-minute detail for about 30 days; older days are hourly summaries. For this month an outage is only visible when every check in an hour failed, so shorter outages are missed and the real uptime may be lower than shown.';
export const pct = (v) => (v == null ? '-' : `${v.toFixed(3)}%`);
export const mins = (v) => (v == null ? '-' : v >= 120 ? `${Math.floor(v / 60)} h ${Math.round(v % 60)} min` : `${Math.round(v * 10) / 10} min`);
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const when = (d) => (d ? new Date(d).toLocaleString([], { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC' : '-');

// Downtime minutes per day: what counted (red) and what was taken off for maintenance (grey)
function DayBars({ days, budget }) {
  const W = 900; const H = 150; const M = { left: 44, right: 8, top: 8, bottom: 22 };
  const top = Math.max(budget, ...days.map((d) => d.down_minutes), 5);
  const bw = (W - M.left - M.right) / days.length;
  const y = (v) => M.top + (1 - v / top) * (H - M.top - M.bottom);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Downtime minutes per day" style={{ display: 'block' }}>
      {[0, top / 2, top].map((g) => (<g key={g}><line x1={M.left} x2={W - M.right} y1={y(g)} y2={y(g)} stroke="#e0e0e0" /><text x={M.left - 6} y={y(g) + 4} textAnchor="end" fontSize="11" fill="#616161">{Math.round(g)}</text></g>))}
      {days.map((d, i) => {
        const counted = Math.max(d.down_minutes - d.excluded_minutes, 0);
        const x = M.left + i * bw + 1;
        return (
          <g key={d.day}>
            <title>{`${d.day}: ${d.uptime_percent == null ? 'no data' : `${pct(d.uptime_percent)}, ${mins(counted)} counted, ${mins(d.excluded_minutes)} maintenance`}`}</title>
            {d.seen_minutes === 0 && <rect x={x} y={H - M.bottom - 3} width={Math.max(bw - 2, 1)} height={3} fill="#bdbdbd" />}
            {counted > 0 && <rect x={x} y={y(counted)} width={Math.max(bw - 2, 1)} height={H - M.bottom - y(counted)} fill="#d32f2f" />}
            {d.excluded_minutes > 0 && <rect x={x} y={y(counted + d.excluded_minutes)} width={Math.max(bw - 2, 1)} height={y(counted) - y(counted + d.excluded_minutes)} fill="#9e9e9e" />}
            {(i % 5 === 0 || i === days.length - 1) && <text x={x + bw / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="#616161">{Number(d.day.slice(8))}</text>}
          </g>
        );
      })}
      <line x1={M.left} x2={W - M.right} y1={y(budget)} y2={y(budget)} stroke="#ed6c02" strokeDasharray="4 4" />
    </svg>
  );
}

// One customer's month: figures, days, outages, maintenance taken off, and the months before it
function UptimeDetailDialog({ customer, year, month, onClose }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let current = true;
    setData(null); setError(null);
    uptimeAPI.customer(customer.customer_id, year, month).then((r) => { if (current) setData(r.data); }).catch((e) => { if (current) setError(errText(e, 'Could not load the month')); });
    uptimeAPI.history(customer.customer_id).then((r) => { if (current) setHistory(r.data); }).catch(() => {});
    return () => { current = false; };
  }, [customer.customer_id, year, month]);

  const s = data?.summary || customer;
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="uptime-title">
      <DialogTitle id="uptime-title" sx={{ pr: 6 }}>
        {customer.code} - {customer.name}: {MONTHS[month - 1]} {year}
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{customer.url}</Typography>
        <IconButton aria-label="Close uptime detail" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Chip color={(STATUS[s.status] || STATUS.no_data).color} label={`${(STATUS[s.status] || STATUS.no_data).label}: ${pct(s.uptime_percent)}`} />
              <Chip variant="outlined" label={`Counted downtime ${mins(s.counted_minutes)}`} />
              <Chip variant="outlined" label={`Maintenance taken off ${mins(s.excluded_minutes)}`} />
              <Chip variant="outlined" label={`Allowed ${mins(s.budget_minutes)} (${mins(s.budget_left_minutes)} left)`} />
              <Chip variant="outlined" color={s.coverage_percent < 95 ? 'warning' : 'default'} label={`Data ${s.coverage_percent}% of the month so far`} />
            </Box>

            {s.resolution_seconds > 300 && <Alert severity="warning" sx={{ mb: 2 }}>{HOURLY_NOTE}</Alert>}
            <Typography variant="subtitle2">Downtime per day (minutes)</Typography>
            <DayBars days={data.days} budget={s.budget_minutes} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>Red counted, grey taken off for maintenance, dashed line the whole month's allowance. Hover a day for its figure.</Typography>

            <Typography variant="subtitle2">Outages</Typography>
            {data.outages.length === 0 ? <Typography variant="body2" sx={{ mb: 2 }}>No outages this month.</Typography> : (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Started (UTC)</TableCell><TableCell>Ended (UTC)</TableCell><TableCell align="right">Length</TableCell><TableCell align="right">Maintenance</TableCell><TableCell align="right">Counted</TableCell><TableCell>What failed</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.outages.map((o) => (
                      <TableRow key={o.started_at}>
                        <TableCell>{when(o.started_at)}</TableCell><TableCell>{when(o.ended_at)}</TableCell>
                        <TableCell align="right">{mins(o.minutes)}</TableCell><TableCell align="right">{mins(o.excluded_minutes)}</TableCell><TableCell align="right">{mins(o.counted_minutes)}</TableCell>
                        <TableCell>{(o.result || 'unknown').replace(/_/g, ' ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {data.maintenance.length > 0 && (
              <>
                <Typography variant="subtitle2">Maintenance windows this month</Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableBody>
                    {data.maintenance.map((m) => (
                      <TableRow key={m.id}><TableCell>{when(m.starts_at)} to {when(m.ends_at)}</TableCell><TableCell>{m.customer_id ? 'This customer' : 'All customers'}</TableCell><TableCell>{m.reason}{m.ticket ? ` (${m.ticket})` : ''}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {history.length > 0 && (
              <>
                <Typography variant="subtitle2">Month by month</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Uptime</TableCell><TableCell>Result</TableCell><TableCell align="right">Counted downtime</TableCell><TableCell align="right">Maintenance</TableCell><TableCell align="right">Outages</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...history].reverse().map((h) => (
                        <TableRow key={`${h.year}-${h.month}`}>
                          <TableCell>{MONTHS[h.month - 1]} {h.year}{h.resolution_seconds > 300 && <Tooltip title={HOURLY_NOTE}><Chip size="small" color="warning" label="Hourly detail" sx={{ ml: 1 }} /></Tooltip>}</TableCell><TableCell align="right">{pct(h.uptime_percent)}</TableCell>
                          <TableCell><Chip size="small" color={(STATUS[h.status] || STATUS.no_data).color} label={(STATUS[h.status] || STATUS.no_data).label} /></TableCell>
                          <TableCell align="right">{mins(h.counted_minutes)}</TableCell><TableCell align="right">{mins(h.excluded_minutes)}</TableCell><TableCell align="right">{h.outages}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UptimeDetailDialog;
